'use client';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PauseBanner } from '@/features/cards/primitives/PauseBanner/PauseBanner';
import { useSubscription } from '@/hooks';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { isPauseBlockedRoute } from '@/shared/lib/auth/permissions';
import { openCrispChat } from '@/shared/lib/crisp';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef } from 'react';

const CLOCK_SKEW_GRACE_MS = 5 * 60 * 1000;

function isAccessDead(endAtIso: string | null | undefined): boolean {
  if (!endAtIso) return false;
  const t = new Date(endAtIso).getTime();
  if (Number.isNaN(t)) return false;
  return t - CLOCK_SKEW_GRACE_MS <= Date.now();
}

/**
 * Full-screen non-dismissible modal blocking the app whenever the member is
 * paused AND access has expired (`whopStatus === 'paused' && end_at < now`).
 * Mounted at platform + account layout levels. Whitelist exempts
 * /account/subscription (already shows inline PauseBanner) and /legal/*.
 *
 * The gate only opens in AccessDead state, where PauseBanner renders the
 * support-only branch — Whop resume never charges immediately or
 * retroactively, so the only way to restore access mid-pause is support
 * intervention or the natural cron at paused_until.
 */
export function PauseGate({ children }: { children: React.ReactNode }) {
  // Chrome-safe pathname: null at prerender/first render — `open` is false
  // then anyway (subscription data is client-fetched), so no behavior change.
  const pathname = useChromePathname();
  const { whopStatus, currentPeriodEnd, pausedUntil } = useSubscription();
  const viewedRef = useRef(false);

  // Treat BE deferred-pause `pause_scheduled` the same as `paused` — the gate
  // trigger is "member opted into a pause AND access has died," which holds
  // regardless of whether the Whop API call has fired yet.
  const isPaused = whopStatus === 'paused' || whopStatus === 'pause_scheduled';
  // Short-circuit before isAccessDead: it reads Date.now() at render, which
  // instant-shell validation flags — only paused members may pay that cost.
  const open =
    isPaused &&
    isAccessDead(currentPeriodEnd) &&
    pathname !== null &&
    isPauseBlockedRoute(pathname);

  useEffect(() => {
    if (open && !viewedRef.current) {
      viewedRef.current = true;
      posthog.capture(AnalyticsEvent.SUBSCRIPTION_PAUSE_GATE_VIEWED, {
        pathname,
      });
    }
    if (!open) {
      viewedRef.current = false;
    }
  }, [open, pathname]);

  const handleSupport = useCallback(() => {
    posthog.capture(AnalyticsEvent.SUBSCRIPTION_PAUSE_SUPPORT_CLICKED, {
      access_state: 'dead',
      source: 'pause_gate',
    });
    openCrispChat('subscription_settings', { pageContext: 'pause_gate' });
  }, []);

  return (
    <>
      {children}
      {/* One step above --z-overlay: platform-shell mounts this gate alongside the @browse
          route modal (z-[10000]), and the gate must win over it. */}
      <Dialog open={open}>
        <DialogContent
          showClose={false}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="max-w-xl p-0 bg-transparent border-0 shadow-none rounded-none z-[10001]"
          overlayClassName="z-[10001]"
        >
          <PauseBanner
            pausedUntil={pausedUntil ?? null}
            endAt={currentPeriodEnd ?? null}
            onResume={() => {}}
            onContactSupport={handleSupport}
            isResuming={false}
            variant="modal"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
